/**
 * Enhanced Conversions - PII hashing for improved conversion matching
 *
 * Google Ads Enhanced Conversions uses hashed first-party customer data
 * (email, phone, name, address) to improve conversion attribution accuracy.
 *
 * All PII is hashed client-side using SHA-256 before being sent to Google.
 * @see https://support.google.com/google-ads/answer/9888656
 */

/**
 * User data input for enhanced conversions
 */
export interface EnhancedUserData {
    email?: string
    phone?: string
    firstName?: string
    lastName?: string
    address?: {
        street?: string
        city?: string
        region?: string // State/Province code (e.g., 'CA')
        postalCode?: string
        country?: string // ISO 3166-1 alpha-2 (e.g., 'US')
    }
}

/**
 * Hashed user data structure for GA4 enhanced conversions
 */
export interface HashedUserData {
    sha256_email_address?: string
    sha256_phone_number?: string
    address?: {
        sha256_first_name?: string
        sha256_last_name?: string
        sha256_street?: string
        city?: string
        region?: string
        postal_code?: string
        country?: string
    }
}

/**
 * Hash a string using SHA-256 via Web Crypto API
 */
export async function hashSHA256(value: string): Promise<string> {
    if (typeof window === 'undefined' || !window.crypto?.subtle) {
        // Fallback for SSR or unsupported browsers
        console.warn('Web Crypto API not available for hashing')
        return ''
    }

    const encoder = new TextEncoder()
    const data = encoder.encode(value)
    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Normalize email for hashing
 * - Lowercase
 * - Trim whitespace
 * - Remove dots from Gmail local part (before @)
 * - Remove plus aliases (e.g., user+tag@gmail.com -> user@gmail.com)
 */
export function normalizeEmail(email: string): string {
    if (!email) return ''

    let normalized = email.toLowerCase().trim()

    // Handle Gmail-specific normalization
    const [localPart, domain] = normalized.split('@')
    if (!localPart || !domain) return normalized

    if (domain === 'gmail.com' || domain === 'googlemail.com') {
        // Remove dots and plus aliases from Gmail addresses
        const cleanLocal = localPart.split('+')[0].replace(/\./g, '')
        normalized = `${cleanLocal}@${domain}`
    }

    return normalized
}

/**
 * Normalize phone number to E.164 format
 * - Remove all non-digit characters except leading +
 * - Ensure starts with country code
 */
export function normalizePhone(phone: string): string {
    if (!phone) return ''

    // Remove all non-digit characters except leading +
    let normalized = phone.replace(/[^\d+]/g, '')

    // Ensure + prefix if it looks like it has a country code
    if (normalized.startsWith('1') && normalized.length === 11) {
        normalized = `+${normalized}`
    } else if (!normalized.startsWith('+') && normalized.length === 10) {
        // Assume US number if 10 digits
        normalized = `+1${normalized}`
    }

    return normalized
}

/**
 * Normalize name for hashing
 * - Lowercase
 * - Trim whitespace
 * - Remove extra spaces
 */
export function normalizeName(name: string): string {
    if (!name) return ''
    return name.toLowerCase().trim().replace(/\s+/g, ' ')
}

/**
 * Prepare and hash user data for enhanced conversions
 * Returns data ready to be passed to GA4 Measurement Protocol user_data field
 */
export async function prepareUserData(
    userData: EnhancedUserData,
): Promise<HashedUserData> {
    const result: HashedUserData = {}

    // Hash email
    if (userData.email) {
        const normalizedEmail = normalizeEmail(userData.email)
        if (normalizedEmail) {
            result.sha256_email_address = await hashSHA256(normalizedEmail)
        }
    }

    // Hash phone
    if (userData.phone) {
        const normalizedPhone = normalizePhone(userData.phone)
        if (normalizedPhone) {
            result.sha256_phone_number = await hashSHA256(normalizedPhone)
        }
    }

    // Handle address data
    if (
        userData.firstName ||
        userData.lastName ||
        userData.address?.street ||
        userData.address?.city
    ) {
        result.address = {}

        if (userData.firstName) {
            const normalizedFirst = normalizeName(userData.firstName)
            if (normalizedFirst) {
                result.address.sha256_first_name =
                    await hashSHA256(normalizedFirst)
            }
        }

        if (userData.lastName) {
            const normalizedLast = normalizeName(userData.lastName)
            if (normalizedLast) {
                result.address.sha256_last_name =
                    await hashSHA256(normalizedLast)
            }
        }

        if (userData.address?.street) {
            const normalizedStreet = userData.address.street
                .toLowerCase()
                .trim()
            if (normalizedStreet) {
                result.address.sha256_street =
                    await hashSHA256(normalizedStreet)
            }
        }

        // Non-hashed address fields (required by Google)
        if (userData.address?.city) {
            result.address.city = userData.address.city.toLowerCase().trim()
        }
        if (userData.address?.region) {
            result.address.region = userData.address.region.toUpperCase().trim()
        }
        if (userData.address?.postalCode) {
            result.address.postal_code = userData.address.postalCode
                .replace(/\s/g, '')
                .toUpperCase()
        }
        if (userData.address?.country) {
            result.address.country = userData.address.country
                .toUpperCase()
                .trim()
        }
    }

    return result
}
